import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import DownloadButton from '@/components/common/download-button/DownloadButton';
import SelectedDeckHeader from '@/components/common/selected-deck-header/SelectedDeckHeader';
import { useDeck } from '@/app/context/DeckContext/useDeck';
import { DeckStatus } from '@/app/constants/DeckStatus';
import Loader from '@/components/common/loader/Loader';
import QuizIcon from '@/icons/QuizIcon';
import FlashcardsIcon from '@/icons/FlashcardsIcon';
import FileEditIcon from '@/icons/FileEditIcon';
import ArticleIcon from '@/icons/ArticleIcon';
import { DeckPages } from '@/components/common/selected-deck-header/models';
import AnkiConnectButton from './anki-connect-button/AnkiConnectButton';
import GenerateForm from './generate-form/GenerateForm';
import SimpleDeckMenuItem from './simple-deck-menu-item/SimpleDeckMenuItem';
import { GeneratorOptions } from '@/app/types/Generator/GeneratorOptions';
import PageLayout from '../common/page-layout/PageLayout';
import { useFeedback } from '@/app/context/FeedbackContext/useFeedback';
import { useCustomer } from '@/app/context/CustomerContext/useCustomer';
import { ShareIcon } from '@heroicons/react/24/solid';
import UserShareIcon from '@/app/icons/UserShareIcon';
import { useSettings } from '@/app/context/SettingsContext/useSettings';
import DownloadAnkiButton from '../common/download-anki-button/DownloadAnkiButton';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';

const DeckHome = () => {
  const { trackEvent, analyticsInstance } = useAnalytics();

  const { deleteDeck, generateFlashcards, selectedDeckLoading, selectedDeck } =
    useDeck();

  const { isPro, isProEligible } = useCustomer();

  const { trackDeckActivity } = useFeedback();

  const { theme } = useSettings();

  const router = useRouter();

  useEffect(() => {
    if (selectedDeck && selectedDeck.status) {
      if (selectedDeck.id && selectedDeck.cardCount > 0) {
        trackEvent(analyticsInstance, 'completed_deck_opened', {
          deck_id: selectedDeck.id,
          language: selectedDeck.language?.code ?? '',
          file_type: selectedDeck.fileType,
          card_count: selectedDeck.cardCount,
          status: selectedDeck.status,
          image_occlusion: selectedDeck.isImageOcclusion ? 1 : 0
        });
        trackActivity('open');
      }

      if (selectedDeck.id && selectedDeck.status === DeckStatus.Failed) {
        trackEvent(analyticsInstance, 'failed_deck_opened', {
          deck_id: selectedDeck.id,
          language: selectedDeck.language?.code ?? '',
          file_type: selectedDeck.fileType,
          card_count: selectedDeck.cardCount,
          status: selectedDeck.status,
          image_occlusion: selectedDeck.isImageOcclusion ? 1 : 0
        });
        trackActivity('failed');
      }

      if (
        selectedDeck.id &&
        selectedDeck.cardCount === 0 &&
        selectedDeck.status === DeckStatus.Complete
      ) {
        trackEvent(analyticsInstance, 'empty_deck_opened', {
          deck_id: selectedDeck.id,
          language: selectedDeck.language?.code ?? '',
          file_type: selectedDeck.fileType,
          card_count: selectedDeck.cardCount,
          status: selectedDeck.status,
          image_occlusion: selectedDeck.isImageOcclusion ? 1 : 0
        });
        trackActivity('empty');
      }
    }
  }, [selectedDeck?.id, selectedDeck?.cardCount]);

  if (!selectedDeck || !selectedDeck.status) {
    return null;
  }

  const trackActivity = (activity: string) => {
    trackDeckActivity({
      deckId: selectedDeck.id,
      language: selectedDeck.language?.code ?? '',
      fileType: selectedDeck.fileType,
      cardCount: selectedDeck.cardCount,
      activityName: activity,
      eventName: 'init',
      isPro,
      isProEligible,
      source: 'v1'
    });
  };

  const onDelete = () => {
    trackActivity('delete');
    trackEvent(analyticsInstance, 'empty_deck_click_delete', {
      deck_id: selectedDeck.id
    });
    deleteDeck(selectedDeck.id, true);
  };

  const isPdf = selectedDeck.name?.toLowerCase().endsWith('pdf') || false;

  const isValid =
    selectedDeck &&
    selectedDeck.status === DeckStatus.Complete &&
    selectedDeck.cardCount > 0;

  let body = null;

  let showingDeck = false;

  if (
    !isValid &&
    (selectedDeck.status === DeckStatus.PendingUpload ||
      selectedDeck.status === DeckStatus.Failed)
  ) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex flex-col justify-center text-lg font-semibold text-secondary-content">
            <div className="text-center text-xl">
              Deck Failed{' '}
              {selectedDeck.errorMessage
                ? '- ' + selectedDeck.errorMessage
                : ''}
            </div>
            <div
              onClick={() => deleteDeck(selectedDeck?.id, true)}
              className="text-md btn mt-4 text-center"
            >
              Delete deck
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!isValid && selectedDeck.status === DeckStatus.Preprocessing) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex items-center text-lg font-semibold text-secondary-content">
            <Loader />
            <div className="ml-2">
              <div>Getting your file ready...</div>
              <div className="mt-1 text-sm">
                This should only take a few seconds
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!isValid && selectedDeck.status === DeckStatus.Ready) {
    body = <GenerateForm />;
  } else if (!isValid && selectedDeck.status === DeckStatus.Generating) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex items-center text-lg font-semibold text-secondary-content">
            <Loader />
            <div className="ml-2">
              <div>
                {selectedDeck.isImageOcclusion
                  ? 'Generating Occlusions'
                  : 'Generating Flashcards'}
                ...
              </div>
              <div className="mt-1 text-sm">This may take a few minutes</div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (selectedDeckLoading) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex items-center text-lg font-semibold text-secondary-content">
            <Loader />
            <div className="ml-2">
              <div>Loading Flashards...</div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (
    !isValid &&
    selectedDeck.status === DeckStatus.Complete &&
    selectedDeck.cardCount === 0
  ) {
    body = (
      <div>
        <div className="mt-24 flex flex-1 flex-col items-center justify-center p-6 md:mt-72">
          <div className="mt-2 flex flex-col items-center text-lg font-semibold text-secondary-content">
            <div className="text-center text-xl">Sorry!</div>
            {selectedDeck.language?.supported && (
              <div className="mt-2 text-center text-lg">
                Limbiks was unable to generate any flashcards.
              </div>
            )}
            {!selectedDeck.language?.supported &&
              !selectedDeck.language?.clozeSupported && (
                <div className="mt-2 text-center text-lg">
                  The detected language, {selectedDeck.language?.name}, is not
                  yet supported.
                </div>
              )}
            {!selectedDeck.language?.supported &&
              selectedDeck.language?.clozeSupported && (
                <div className="mt-2 text-center text-lg font-normal">
                  The detected language, {selectedDeck.language?.name}, is only
                  supported for <b>Fill in the blank</b> flashcards.
                </div>
              )}
            <div onClick={onDelete} className="text-md btn mt-4 text-center">
              Delete deck
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    showingDeck = true;
    body = (
      <div className="flex h-full justify-center overflow-auto">
        <div className="m-8 grid max-w-3xl grid-cols-1 gap-8 text-center md:grid-cols-2 lg:pt-8">
          <SimpleDeckMenuItem
            onClick={() => {
              trackActivity('quiz');
              trackEvent(analyticsInstance, 'deck_click_quiz', {
                file_type: selectedDeck.fileType,
                card_count: selectedDeck.cardCount,
                deck_id: selectedDeck.id
              });
              router.push(`/app/${selectedDeck.id}/quiz`);
            }}
            icon={<QuizIcon invert={theme === 'light'} />}
            title="Multiple Choice"
          />
          <SimpleDeckMenuItem
            onClick={() => {
              trackActivity('flashcards');
              trackEvent(analyticsInstance, 'deck_click_flashcards', {
                file_type: selectedDeck.fileType,
                card_count: selectedDeck.cardCount,
                deck_id: selectedDeck.id
              });
              router.push(`/app/${selectedDeck.id}/flashcards`);
            }}
            icon={<FlashcardsIcon />}
            title="Flashcards"
          />
          <SimpleDeckMenuItem
            onClick={() => {
              trackActivity('studyGuide');
              trackEvent(analyticsInstance, 'deck_click_study_guide', {
                file_type: selectedDeck.fileType,
                card_count: selectedDeck.cardCount,
                deck_id: selectedDeck.id
              });
              router.push(`/app/${selectedDeck.id}/studyGuide`);
            }}
            icon={<ArticleIcon />}
            title="Study Guide"
          />
          <SimpleDeckMenuItem
            onClick={() => {
              trackActivity('edit');
              trackEvent(analyticsInstance, 'deck_click_edit', {
                file_type: selectedDeck.fileType,
                card_count: selectedDeck.cardCount,
                deck_id: selectedDeck.id
              });
              router.push(`/app/${selectedDeck.id}/edit`);
            }}
            icon={<FileEditIcon />}
            title="View All / Edit"
          />
          {selectedDeck.isImageOcclusion ? (
            <DownloadAnkiButton
              iconOnly={false}
              isLanding={true}
              onClick={() => {
                trackActivity('download_anki');
                trackEvent(
                  analyticsInstance,
                  'deck_click_download_anki_image_occlusion',
                  {
                    file_type: selectedDeck.fileType,
                    card_count: selectedDeck.cardCount,
                    deck_id: selectedDeck.id
                  }
                );
              }}
            />
          ) : (
            <DownloadButton
              iconOnly={false}
              isLanding={true}
              onClick={() => {
                trackActivity('download_anki');
                trackEvent(analyticsInstance, 'deck_click_download_csv', {
                  file_type: selectedDeck.fileType,
                  card_count: selectedDeck.cardCount,
                  deck_id: selectedDeck.id
                });
              }}
            />
          )}

          <AnkiConnectButton
            onClick={() => {
              trackEvent(analyticsInstance, 'deck_click_anki_connect', {
                file_type: selectedDeck.fileType,
                card_count: selectedDeck.cardCount,
                deck_id: selectedDeck.id
              });
              trackActivity('anki');
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <PageLayout authenticated>
      <div>
        <SelectedDeckHeader
          enableSharing={!!showingDeck}
          currentPage={DeckPages.Home}
          onBack={() => {
            trackActivity('back');
            router.back();
          }}
        ></SelectedDeckHeader>
        <div className="mt-[132px] w-full">{body}</div>
      </div>
    </PageLayout>
  );
};

export default DeckHome;
